import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RequestStatus } from 'shared/constants/State';
import {
  Physician,
  PlansSuccess,
  Product,
  VoucherData,
  CouponData,
} from '../services/types';
import { BillingInfo, PhysiciansByAddon, SubscriptionFormData } from './types';

export type CheckoutState = {
  status?: RequestStatus;
  products?: PlansSuccess;
  physicians?: Physician[];
  mostSeller?: string;
  isAnnual: boolean;
  offCanvasOpen?: boolean;
  selectedPlan?: Product;
  selectedAddons?: Product[];
  physiciansByAddon?: PhysiciansByAddon;
  subscriptionStatus?: RequestStatus;
  flipCard: boolean;
  subscriptionStepMapper?: string[];
  currentSubscriptionStep?: string;
  errorsMessage?: string[];
  addressData?: BillingInfo;
  appliedVoucher?: string;
  applyVoucherStatus?: RequestStatus;
  removeVoucherStatus?: RequestStatus;
  voucherErrorMessage: string;
  isVoucher: boolean;
  coupon?: CouponData;
  isVoucherOrCouponApplied: boolean;
};

export const initialState: CheckoutState = {
  status: RequestStatus.Pending,
  mostSeller: 'plus',
  selectedAddons: [],
  isAnnual: false,
  offCanvasOpen: false,
  flipCard: false,
  appliedVoucher: '',
  applyVoucherStatus: RequestStatus.Pending,
  removeVoucherStatus: RequestStatus.Pending,
  voucherErrorMessage: '',
  isVoucher: false,
  isVoucherOrCouponApplied: false,
};

export const nameStore = '@webapp/checkout';

export const fetchCheckoutData = createAction(`${nameStore}/fetchCheckoutData`);

export const fetchCheckoutProducts = createAction(
  `${nameStore}/fetchCheckoutProducts`,
);

export const selectAddonTrigger = createAction<Product>(
  `${nameStore}/selectAddonTrigger`,
);

export const triggerSubscription = createAction<SubscriptionFormData>(
  `${nameStore}/triggerSubscription`,
);

export const triggerVerifyInitialStep = createAction(
  `${nameStore}/triggerVerifyInitialStep`,
);

export const changeSubscriptionStep = createAction(
  `${nameStore}/changeSubscriptionStep`,
);

export const applyVoucher = createAction<VoucherData>(
  `${nameStore}/applyVoucher`,
);

export const removeVoucher = createAction<VoucherData>(
  `${nameStore}/removeVoucher`,
);

const CheckoutSlice = createSlice({
  name: nameStore,
  initialState,
  reducers: {
    fetchCheckoutProductsSuccess: (
      state,
      action: PayloadAction<{
        products: PlansSuccess;
      }>,
    ): CheckoutState => ({
      ...state,
      status: RequestStatus.Success,
      products: action.payload.products,
    }),
    fetchCheckoutDataSuccess: (
      state,
      action: PayloadAction<{
        products: PlansSuccess;
        physicians: Physician[];
      }>,
    ): CheckoutState => ({
      ...state,
      status: RequestStatus.Success,
      products: action.payload.products,
      physicians: action.payload.physicians,
    }),
    fetchCheckoutDataFailure: (state): CheckoutState => ({
      ...state,
      status: RequestStatus.Error,
    }),
    toggleAnnualOrMonthlyPaymentRecurrence: (state) => ({
      ...state,
      isAnnual: !state.isAnnual,
    }),
    setIsAnnual: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isAnnual: action.payload,
    }),
    updateSelectedPlan: (state, action: PayloadAction<Product>) => ({
      ...state,
      selectedPlan: action.payload,
    }),
    updateSelectedPhysicians: (
      state,
      action: PayloadAction<PhysiciansByAddon | undefined>,
    ) => ({
      ...state,
      physiciansByAddon: action.payload && {
        ...state.physiciansByAddon,
        ...action.payload,
      },
    }),
    updateSubscriptionStatus: (
      state,
      action: PayloadAction<RequestStatus>,
    ) => ({
      ...state,
      subscriptionStatus: action.payload,
    }),
    toggleOffCanvasOpenClose: (state) => ({
      ...state,
      offCanvasOpen: !state.offCanvasOpen,
    }),
    toggleFlipCard: (state) => ({
      ...state,
      flipCard: !state.flipCard,
    }),
    updateSelectedAddons: (state, action: PayloadAction<Product[]>) => ({
      ...state,
      selectedAddons: action.payload,
    }),
    updateSubscriptionStepMapper: (
      state,
      action: PayloadAction<string[] | undefined>,
    ) => ({
      ...state,
      subscriptionStepMapper: action.payload,
    }),
    updateCurrentSubscriptionStep: (
      state,
      action: PayloadAction<string | undefined>,
    ) => ({
      ...state,
      currentSubscriptionStep: action.payload,
    }),
    setErrorsMessage: (state, action: PayloadAction<string[] | undefined>) => ({
      ...state,
      errorsMessage: action.payload,
    }),
    saveAddressData: (state, action: PayloadAction<BillingInfo>) => ({
      ...state,
      addressData: action.payload,
    }),
    applyCouponSuccess: (state, action: PayloadAction<string>) => ({
      ...state,
      coupon: { code: action.payload, is_voucher: false },
    }),
    applyVoucher: (state, action) => ({
      ...state,
      applyVoucherStatus: RequestStatus.Pending,
      appliedVoucher: action.payload.voucher,
    }),
    setAppliedVoucher: (state, action) => ({
      ...state,
      appliedVoucher: action.payload,
    }),
    setIsVoucherOrCouponApplied: (state, action) => ({
      ...state,
      isVoucherOrCouponApplied: action.payload,
    }),
    applyVoucherSuccess: (state, action: PayloadAction<string>) => ({
      ...state,
      applyVoucherStatus: RequestStatus.Success,
      appliedVoucher: action.payload,
      voucherErrorMessage: '',
      isVoucher: true,
    }),
    applyVoucherFailure: (state, action: PayloadAction<string>) => ({
      ...state,
      applyVoucherStatus: RequestStatus.Error,
      voucherErrorMessage: action.payload,
    }),
    removeVoucher: (state) => ({
      ...state,
      removeVoucherStatus: RequestStatus.Pending,
      appliedVoucher: '',
    }),
    removeVoucherSuccess: (state) => ({
      ...state,
      removeVoucherStatus: RequestStatus.Success,
      appliedVoucher: '',
      voucherErrorMessage: '',
    }),
    removeVoucherFailure: (state, action: PayloadAction<string>) => ({
      ...state,
      removeVoucherStatus: RequestStatus.Error,
      voucherErrorMessage: action.payload,
    }),
    fetchCheckoutCouponData: (
      state,
      action: PayloadAction<{
        coupon: CouponData;
      }>,
    ): CheckoutState => ({
      ...state,
      status: RequestStatus.Success,
      coupon: action.payload.coupon,
      isVoucher: action.payload.coupon.is_voucher,
    }),
  },
});

export const {
  fetchCheckoutDataSuccess,
  fetchCheckoutDataFailure,
  toggleAnnualOrMonthlyPaymentRecurrence,
  updateSelectedPlan,
  updateSelectedAddons,
  updateSelectedPhysicians,
  updateSubscriptionStatus,
  toggleOffCanvasOpenClose,
  toggleFlipCard,
  updateSubscriptionStepMapper,
  updateCurrentSubscriptionStep,
  setErrorsMessage,
  setIsAnnual,
  fetchCheckoutProductsSuccess,
  saveAddressData,
  applyCouponSuccess,
  setAppliedVoucher,
  applyVoucherSuccess,
  applyVoucherFailure,
  removeVoucherSuccess,
  removeVoucherFailure,
  fetchCheckoutCouponData,
  setIsVoucherOrCouponApplied,
} = CheckoutSlice.actions;

export default CheckoutSlice.reducer;
